export const toolGroups = [
  {
    label: "Automation",
    tools: [
      { name: "Playwright", accent: true },
      { name: "Cypress",    accent: true },
      { name: "JavaScript", accent: false },
      { name: "TypeScript", accent: false },
    ],
  },
  {
    label: "CI/CD & Pipeline",
    tools: [
      { name: "GitHub Actions",    accent: true },
      { name: "GitLab CI",         accent: true },
      { name: "Slack Integrations",accent: false },
    ],
  },
  {
    label: "API & Data",
    tools: [
      { name: "Postman",   accent: false },
      { name: "SQL",       accent: false },
      { name: "MongoDB",   accent: false },
      { name: "REST APIs", accent: false },
    ],
  },
  {
    label: "Tools",
    tools: [
      { name: "TestRail",     accent: false },
      { name: "JIRA",         accent: false },
      { name: "Datadog",      accent: false },
      { name: "BrowserStack", accent: false },
      { name: "LaunchDarkly", accent: false },
      { name: "Figma",        accent: false },
    ],
  },
  {
    label: "Methodology",
    tools: [
      { name: "Agile",        accent: false },
      { name: "Scrum",        accent: false },
      { name: "Shift-Left",   accent: true },
      { name: "Risk-Based Testing", accent: false },
    ],
  },
  {
    label: "AI & Agents",
    tools: [
      { name: "Claude",         accent: false },
      { name: "GitHub Copilot", accent: false },
      { name: "Cursor",         accent: false },
      { name: "Agents",         accent: false },
      { name: "Skills",         accent: false },
    ],
  },
];

export const experiences = [
  {
    company: "ImageKit",
    role: "SDET, sole QA owner",
    date: "Jan 2024 to July 2026",
    badge: "Remote",
    year: "Since 2024",
    intro: "ImageKit is a SaaS company headquartered in India and used by thousands of companies globally. I’m the sole QA owner across the product.",
    points: [
      "Built the Playwright automation suite from scratch and integrated it into CI, including custom Slack reporting so failures reach the right people with enough context to act quickly.",
      "Created the automated smoke suite for critical happy paths, protecting the highest-value user journeys on every change and catching broken core flows before release.",
      "Own triage, release sign-off, reverts, and manual testing for every feature before it ships, which matters more as the DAM product keeps expanding while the engineering team stays deliberately lean.",
      "Balance speed and risk during release decisions, signing off low-risk work quickly while holding changes that could affect delivery until the right engineer or stakeholder has weighed in.",
    ],
    tags: [
      { name: "Playwright",             green: true },
      { name: "CI/CD",                  green: true },
      { name: "Custom Slack Reporting", green: true },
      { name: "DAM",                    green: false },
      { name: "API Testing",            green: false },
      { name: "Solo QA Ownership",      green: false },
    ],
  },
  {
    company: "Clipboard Health",
    role: "Senior QA and Release Manager",
    date: "Sep 2021 to Jul 2023",
    badge: "Remote · YC",
    year: "2021 to 2023",
    intro: "Clipboard Health is a US healthcare staffing marketplace connecting nurses with facilities, where release quality directly affects shifts, pay, and operations.",
    points: [
      "Grew from manual testing into the release manager role, running roughly 720 production releases across QA, product, and engineering.",
      "Ran release cut, QA sign-off, and hotfix processes, helping teams move quickly without losing control of production risk.",
      "Tested the onboarding revamp end to end on Android and iOS, plus core worker features like shift booking, instant pay, live ETA, and in-app chat.",
      "Contributed to the Selenium automation project alongside the existing QA team.",
    ],
    tags: [
      { name: "Release Management",      green: true },
      { name: "Manual Testing",          green: false },
      { name: "Selenium",                green: false },
      { name: "iOS and Android",         green: false },
      { name: "Cross-team Coordination", green: false },
    ],
  },
  {
    company: "Housr",
    role: "Quality Analyst",
    date: "Aug 2019 to Sep 2021",
    badge: "Gurgaon",
    year: "2019 to 2021",
    intro: "Housr is a managed co-living brand.",
    points: [
      "Built the Cypress automation framework for their website.",
      "Tested the revamped site and mobile app across the payment gateway, meal coupons, and rent management.",
      "Worked with stakeholders to turn business requirements into clear, detailed tickets for the dev team.",
    ],
    tags: [
      { name: "Cypress", green: true },
      { name: "Postman", green: false },
      { name: "SQL",     green: false },
      { name: "API Testing", green: false },
    ],
  },
  {
    company: "Netomi",
    role: "Quality Analyst",
    date: "Apr 2017 to Aug 2019",
    badge: "Gurugram",
    year: "2017 to 2019",
    intro: "Netomi builds AI customer service chatbots.",
    points: [
      "Tested bots for global brands including Pampers, WestJet, Singtel, and Aflac.",
      "Ran functional, regression, and production validation testing.",
      "Reviewed live conversations to find where the bots fell short, and owned defects through to closure.",
    ],
    tags: [
      { name: "Functional Testing", green: false },
      { name: "SQL",                green: false },
      { name: "Chatbots and AI",    green: false },
      { name: "QA Process",         green: false },
    ],
  },
];

export const caseStudies = [
  {
    title: "400+ Tests, 3 Browsers, Zero Manual Regression Days",
    company: "ImageKit.io",
    desc: "I built a Playwright UI and API testing framework from scratch — Page Object Model for maintainability, integrated into GitLab CI then migrated to GitHub Actions with scheduled jobs. The custom Slack report included the latest code commit diff, so when a test failed, the team knew exactly which commit caused it and could fix it immediately. What once took 3 days of manual regression now takes 1 day.",
    pills: [
      { bold: "400+", text: " E2E tests" },
      { bold: "3 days → 1 day", text: " regression" },
      { text: "GitHub Actions CI" },
      { text: "Slack integration" },
    ],
  },
  {
    title: "High Stakes, High Frequency — Keeping Clipboard Health's Releases Clean",
    company: "Clipboard Health",
    desc: "Clipboard Health is a two-sided healthcare marketplace where a broken app doesn't just affect UX — it directly impacts nurses' livelihoods and patient care. Shipping fast and shipping clean were both non-negotiable.\n\nI managed approximately 720 production releases — coordinating across QAs, PMs, and engineering teams via Slack and calls, enforcing a release cut checklist, no last-minute merges, and a hard QA and product sign-off gate. Before this process, releases were chaotic and consistently delayed — I brought structure, predictability, and confidence to every deployment. For hotfixes, the standard release process was bypassed — once QA and product gave the go-ahead, the fix went live immediately.",
    pills: [
      { bold: "720", text: " production releases" },
      { bold: "4–5 teams", text: " coordinated" },
      { text: "Zero critical escapes" },
    ],
  },
];

export const writings = [
  "How I integrated Playwright into CI/CD — and what I learned",
  "Release management in practice: what 2 deploys a week taught me",
  "Shift-left testing — not just a buzzword",
  "Why I still do exploratory testing even when we have 400+ automated tests",
];
